import { z } from "zod";

// ============================================
// COMMON PATTERNS
// ============================================

/** Date format YYYY-MM-DD */
export const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: "Date must be in YYYY-MM-DD format",
});

/** Time format HH:mm */
export const timeSchema = z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
  message: "Time must be in HH:mm format",
});

/** Time format HH:mm with 15-minute intervals */
export const time15MinSchema = z
  .string()
  .regex(/^([0-1][0-9]|2[0-3]):(00|15|30|45)$/, {
    message:
      "Time must be in HH:mm format with 15-minute intervals (00, 15, 30, 45)",
  });

/** Month format YYYY-MM */
export const monthSchema = z.string().regex(/^\d{4}-\d{2}$/, {
  message: "Month must be in YYYY-MM format",
});

/** Hex color format #RRGGBB */
export const hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, {
  message: "Color must be a valid hex color (e.g., #FF5733)",
});

/** Strong password: 8+ chars, uppercase, lowercase, number */
export const strongPasswordSchema = z
  .string()
  .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/, {
    message:
      "Password must be at least 8 characters with uppercase, lowercase, and numbers",
  });

// ============================================
// FIRESTORE TIMESTAMP (for frontend/backend compatibility)
// ============================================

/** Firestore Timestamp can be a Date, Timestamp object, or serialized object */
export const firestoreTimestampSchema = z.any();

// ============================================
// COMMON ENUMS
// ============================================

export const locationEnum = z.enum(["in-house", "on-site"]);
export type Location = z.infer<typeof locationEnum>;

export const visitTypeEnum = z.enum(["work", "sport"]);
export type VisitType = z.infer<typeof visitTypeEnum>;

export const appointmentStatusEnum = z.enum([
  "prenotata",
  "in_attesa",
  "in_corso",
  "effettuata",
  "non_presentato",
]);
export type AppointmentStatus = z.infer<typeof appointmentStatusEnum>;

export const userRoleEnum = z.enum([
  "admin",
  "employee",
  "doctor",
  "corporate",
]);
export type UserRole = z.infer<typeof userRoleEnum>;

export const overrideTypeEnum = z.enum(["block", "extra", "modify"]);
export type OverrideType = z.infer<typeof overrideTypeEnum>;

export const slotStateEnum = z.enum(["available", "blocked", "booked"]);
export type SlotState = z.infer<typeof slotStateEnum>;

export const blockReasonEnum = z.enum(["generic", "company_visit", "region"]);
export type BlockReason = z.infer<typeof blockReasonEnum>;

export const documentTypeEnum = z.enum([
  "idoneita",
  "nomina",
  "sopralluogo",
  "documenti",
  "altro",
]);
export type DocumentType = z.infer<typeof documentTypeEnum>;
